/**
 * Central configuration for coordinate search and CRS guessing.
 * Contains thresholds, geographic bounds, and messaging for coordinate interpretation.
 */

// Germany bounding box in WGS84 (lat/lon)
// Approximate bounds: southernmost point (Gela Island), northernmost (Sylt), west (Netherlands), east (Polish border)
export const GERMANY_BBOX_WGS84 = {
  minLat: 47.27,
  maxLat: 55.06,
  minLon: 5.87,
  maxLon: 15.04,
};

// WGS84 coordinate plausibility ranges (allows some tolerance outside Germany for edge cases)
export const WGS84_RANGES = {
  latMin: 47.0,
  latMax: 55.5,
  lonMin: 5.5,
  lonMax: 15.5,
};

// UTM coordinate plausibility ranges (zone-specific, approximate for German territory)
// Easting can be negative for locations west of the central meridian (e.g. western Germany in UTM32)
export const UTM32_RANGES = {
  easting: { min: -200000, max: 833000 },   // Allow negative easting for westernmost Germany
  northing: { min: 5200000, max: 6100000 }, // Approximate northing range for Germany in zone 32
};

export const UTM33_RANGES = {
  easting: { min: -200000, max: 833000 },   // Allow negative easting for westernmost Germany
  northing: { min: 5200000, max: 6100000 }, // Approximate northing range for Germany in zone 33
};

// Shared UTM ranges (both zones can be plausible in this band)
// This defines the longitude range where both UTM32 and UTM33 can plausibly represent German coordinates
export const UTM_AMBIGUITY_LON_RANGE = {
  minLon: 5.87,  // Shared zone boundary area
  maxLon: 8.0,   // Approximate overlap region
};

// CRS EPSG codes
export const CRS_EPSG = {
  WGS84: "EPSG:4326",
  UTM32: "EPSG:25832",
  UTM33: "EPSG:25833",
};

// Messages and labels for UI display
export const CRS_GUESS_MESSAGES = {
  ambiguityWarning: "Overlap between UTM zones detected",
  guessedLabel: "CRS guessed",
  wgs84Label: "WGS84",
  utm32Label: "UTM 32N",
  utm33Label: "UTM 33N",
};

// Transformation tolerance: coordinates transformed to WGS84 that fall outside this box
// are considered invalid/implausible
export const WGS84_PLAUSIBILITY_BBOX = GERMANY_BBOX_WGS84;

// Buffered Germany bbox used for the coordinate search destination check.
// Adds ~1° of tolerance around the tight bbox to avoid blocking border-area coordinates.
export const GERMANY_BBOX_BUFFERED = {
  minLat: GERMANY_BBOX_WGS84.minLat - 1.0,
  maxLat: GERMANY_BBOX_WGS84.maxLat + 1.0,
  minLon: GERMANY_BBOX_WGS84.minLon - 1.0,
  maxLon: GERMANY_BBOX_WGS84.maxLon + 1.0,
};
